import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { ApiError } from "@/services/api";
import { wizardDraftsApi, type WizardDraft, type WizardKind } from "@/services/wizardDrafts";

/**
 * WIZDRAFT-1 / WIZDRAFT-2 · 新建类向导的草稿自动存 / 还原（产品 / 酒店 / 景点门票 / 供应商 / 同行客户 / 手工下单共用；只管「新建」，编辑已有的不做）。
 * 放在 src/shared：个人站 / 公司站共用，不进会被导出剥离的目录。
 *
 *  · 首次有改动时 POST 拿 id（URL 带 ``?draft=<id>``，刷新还在同一份）；之后每 30 秒节流 PUT + 每次换步骤立即 PUT；
 *  · 「有改动」= 向导状态（step + payload）序列化后和上次存的不一样——没动过就不存，不会白建一堆空草稿；
 *  · 从 ``?draft=<id>`` 进来：GET → ``onRestore`` 还原全部状态并跳到存的那一步；还原完成前一律不存（免得拿空状态覆盖草稿）；
 *  · 存失败不挡操作：状态变 error（红字「草稿没存上，稍后自动重试」），下一个 30 秒会自动重试；
 *  · 保存串行（链式 promise）：POST 还没回来时又触发保存，排队后按最新状态存，不会建出两份草稿；
 *  · ``suspend`` / ``resume``：点「创建」时暂停自动存（创建成功后草稿已被后端同事务删掉，不能再存回去）。
 */
const AUTOSAVE_MS = 30_000;

export interface DraftSnapshot {
  step: number;
  title: string | null;
  payload: Record<string, unknown>;
}

export type DraftStatus = { kind: "idle" } | { kind: "saved"; at: Date } | { kind: "error" };

export function useWizardDraft(opts: {
  kind: WizardKind;
  enabled: boolean;
  step: number;
  title: string | null;
  payload: Record<string, unknown>;
  onRestore: (d: WizardDraft) => void;
}) {
  const { kind, enabled, step, title, payload, onRestore } = opts;
  const qc = useQueryClient();
  const [sp, setSp] = useSearchParams();
  const idParam = sp.get("draft");
  const idRef = useRef<string | null>(enabled ? idParam : null);
  const [ready, setReady] = useState(!(enabled && idParam));
  const [restoreMsg, setRestoreMsg] = useState<string | null>(null);
  const [status, setStatus] = useState<DraftStatus>({ kind: "idle" });
  const [draftId, setDraftId] = useState<string | null>(idRef.current);

  const serialized = useMemo(() => JSON.stringify({ step, payload }), [step, payload]);
  const latest = useRef({ serialized, step, title, payload });
  latest.current = { serialized, step, title, payload };
  const lastSaved = useRef<string | null>(null);   // null = 还没记基线（还原完成 / 第一次渲染后记）
  const suspended = useRef(false);
  const chain = useRef<Promise<unknown>>(Promise.resolve());
  const onRestoreRef = useRef(onRestore);
  onRestoreRef.current = onRestore;

  // 从 ?draft= 进来：还原（只在进页面时做一次——之后第一次自动存会把 id 写进地址栏，不能因为地址栏变了又还原一遍把用户刚改的冲掉）
  const initialId = useRef(idParam);
  useEffect(() => {
    const first = initialId.current;
    if (!enabled || !first) return;
    let alive = true;
    wizardDraftsApi.get(kind, first)
      .then((d) => {
        if (!alive) return;
        lastSaved.current = null;               // 还原后的第一份状态 = 基线
        onRestoreRef.current(d);
        setReady(true);
      })
      .catch((ex) => {
        if (!alive) return;
        idRef.current = null;
        setDraftId(null);
        setSp((p) => { const n = new URLSearchParams(p); n.delete("draft"); return n; }, { replace: true });
        setRestoreMsg(ex instanceof ApiError && ex.status === 404 ? "这份草稿已经不存在（可能被别人删了），已按新建处理。" : "草稿加载失败，已按新建处理。");
        setReady(true);
      });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);

  // 基线：就绪后的第一次渲染记下当前状态（新建 = 空表单；还原 = 刚还原的内容）——只有和它不一样才算「有改动」
  useEffect(() => {
    if (enabled && ready && lastSaved.current === null) lastSaved.current = serialized;
  }, [enabled, ready, serialized]);

  const doSave = useCallback(async (): Promise<boolean> => {
    const cur = latest.current;
    if (!enabled || suspended.current || lastSaved.current === null || cur.serialized === lastSaved.current) return true;
    const body = { title: cur.title, step: cur.step, payload: cur.payload };
    try {
      let id = idRef.current;
      if (id) {
        try {
          await wizardDraftsApi.update(kind, id, body);
        } catch (ex) {
          if (!(ex instanceof ApiError && ex.status === 404)) throw ex;
          if (suspended.current) return true;    // 正在创建（草稿已被后端同事务删掉）：不能再把它建回来
          id = null;                             // 草稿被别人删了：重新建一份，不丢这次的内容
        }
      }
      if (!id) {
        const d = await wizardDraftsApi.create(kind, body);
        if (!d || !d.id) throw new Error("草稿接口没返回 id");
        if (suspended.current) {                 // 这次 POST 在「创建」之后才落地（创建请求已发出、没带它的 id）：它是孤儿，删掉，别留在黄条里
          void wizardDraftsApi.remove(kind, d.id).catch(() => undefined);
          return true;
        }
        idRef.current = d.id;
        setDraftId(d.id);
        setSp((p) => { const n = new URLSearchParams(p); n.set("draft", d.id); return n; }, { replace: true });
      }
      lastSaved.current = cur.serialized;
      setStatus({ kind: "saved", at: new Date() });
      qc.invalidateQueries({ queryKey: ["wizard-drafts", kind] });
      return true;
    } catch {
      setStatus({ kind: "error" });
      return false;
    }
  }, [enabled, kind, qc, setSp]);

  /** 保存串行：排在前一次后面，按「那一刻」的最新状态存 */
  const saveNow = useCallback((): Promise<boolean> => {
    const run = chain.current.then(doSave, doSave);
    chain.current = run;
    return run as Promise<boolean>;
  }, [doSave]);

  // 每 30 秒：有改动就存
  useEffect(() => {
    if (!enabled || !ready) return;
    const t = setInterval(() => { void saveNow(); }, AUTOSAVE_MS);
    return () => clearInterval(t);
  }, [enabled, ready, saveNow]);

  // 换步骤立即存
  const prevStep = useRef(step);
  useEffect(() => {
    if (prevStep.current === step) return;
    prevStep.current = step;
    if (enabled && ready) void saveNow();
  }, [step, enabled, ready, saveNow]);

  // 离开向导页（站内跳走）时最后存一次——异步，页面卸载后继续跑完
  useEffect(() => () => { if (enabled) void saveNow(); }, [enabled, saveNow]);

  return {
    ready,
    draftId,
    status,
    restoreMsg,
    /** 现在的草稿 id（创建产品时带 wizard_draft_id；读 ref，不受渲染时机影响） */
    currentId: () => idRef.current,
    saveNow,
    /** 等已经在排队 / 在途的保存跑完（不触发新的保存）；最多等 ms 毫秒——网络慢也不能把「创建」按钮卡住 */
    settle: (ms = 600): Promise<unknown> => Promise.race([chain.current.catch(() => undefined), new Promise((r) => setTimeout(r, ms))]),
    suspend: () => { suspended.current = true; },
    resume: () => { suspended.current = false; },
  };
}
